import config, { logger } from '../config';
import {
  apiFetch,
  getApiHeaders,
  getToken,
  handleApiError,
  handleApiResponseError,
  updateV2OnboardingAiResult
} from './api';
import {
  calculateAllFinancialDataAI,
  calculatedFinancialDataToV2OnboardingAiResult,
  financialDataFromOnboardingChat,
  getDurationValue,
  getFinancialIndependenceText,
  getHealthcareCostsText,
  getLongevityText,
  getSocialSecurityText,
  getSpouseAgeText,
  normalizeOnboardingProgressKeys,
  onboardingProgressFromV2OnboardingAiResult
} from './onboardingUtils';
import { FINANCIAL_CONSTANTS } from './helper';

const { LIFE_EXPECTANCY, SOCIAL_SECURITY_BENEFIT_AGE } = FINANCIAL_CONSTANTS;

const FALLBACK = {
  duration: '1 year',
  spouseAge: "While your spouse's/partner's age is not specified",
  healthcare: 'Your healthcare costs approximately will be $197,500 lifetime for you.',
  socialSecurity: `Social Security benefits are assumed to start for you at age ${SOCIAL_SECURITY_BENEFIT_AGE}.`,
  longevity: `For planning purposes, this scenario assumes you live up to ${LIFE_EXPECTANCY} years.`,
  financialIndependence: 'The goal is to achieve financial independence and retire.'
};

/** V2 row is `{ financial_profile, updated_at, ...scenario fields }`; map to the same shape as `calculateAllFinancialDataAI`. */
export function toFinancialData(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const payload = v2OnboardingAiResultToFinancialData(data) || data;
  if (
    payload.duration == null &&
    payload.spouseAgeText == null &&
    payload.onboardingProgress == null
  ) {
    return null;
  }
  return {
    ...payload,
    onboardingProgress: normalizeOnboardingProgressKeys(payload.onboardingProgress || {})
  };
}

function v2OnboardingAiResultToFinancialData(v2Result) {
  const rawProgress = onboardingProgressFromV2OnboardingAiResult(v2Result);
  if (!rawProgress) return null;

  const onboardingProgress = normalizeOnboardingProgressKeys(rawProgress);
  const derived = {
    duration: getDurationValue(onboardingProgress) || FALLBACK.duration,
    spouseAgeText: getSpouseAgeText(onboardingProgress) || FALLBACK.spouseAge,
    healthcareCostsText: getHealthcareCostsText(onboardingProgress) || FALLBACK.healthcare,
    socialSecurityText: getSocialSecurityText(onboardingProgress) || FALLBACK.socialSecurity,
    longevityText: getLongevityText(onboardingProgress) || FALLBACK.longevity,
    financialIndependenceText:
      getFinancialIndependenceText(onboardingProgress) || FALLBACK.financialIndependence
  };

  return {
    duration: v2Result.duration ?? derived.duration,
    spouseAgeText: v2Result.spouseAgeText ?? derived.spouseAgeText,
    healthcareCostsText: v2Result.healthcareCostsText ?? derived.healthcareCostsText,
    socialSecurityText: v2Result.socialSecurityText ?? derived.socialSecurityText,
    longevityText: v2Result.longevityText ?? derived.longevityText,
    financialIndependenceText: v2Result.financialIndependenceText ?? derived.financialIndependenceText,
    onboardingProgress
  };
}

function persistCalculatedResult(token, calculatedData, navigate) {
  updateV2OnboardingAiResult(
    {
      token,
      v2OnboardingAiResult: calculatedFinancialDataToV2OnboardingAiResult(calculatedData)
    },
    navigate
  ).then((result) => {
    if (!result.ok) {
      logger.warn('Failed to save calculated data:', result.message || 'Unknown error');
    }
  });
}

/** Older servers only expose the full onboarding bundle. Shape it like the scenario-map route. */
export function scenarioMapFromLegacyBody(body) {
  const data = body?.data;
  if (!body?.success || !data) return null;
  const result = data.v2_onboarding_ai_result;
  const hasResult = result != null && typeof result === 'object' && !Array.isArray(result);
  return {
    onboarding_ai_done: Boolean(data.onboarding_ai_done),
    v2_onboarding_ai_result: hasResult ? result : null,
    ...(hasResult
      ? {}
      : { chat: Array.isArray(data.v2_onboarding_ai?.chat) ? data.v2_onboarding_ai.chat : [] })
  };
}

async function fetchScenarioMap(token, signal) {
  const headers = getApiHeaders({ 'Content-Type': 'application/json' }, token);
  const scenarioResponse = await apiFetch(
    `${config.API_BASE_URL}${config.API_ENDPOINTS.V2_ONBOARDING_AI_SCENARIO_MAP}`,
    { method: 'GET', headers, signal }
  );
  if (scenarioResponse.status !== 404) return scenarioResponse;

  await scenarioResponse.text().catch(() => {});
  logger.warn('Scenario map endpoint unavailable; falling back to the full onboarding payload.');
  const legacyResponse = await apiFetch(
    `${config.API_BASE_URL}${config.API_ENDPOINTS.V2_ONBOARDING_AI}`,
    { method: 'GET', headers, signal }
  );
  if (!legacyResponse.ok) return legacyResponse;

  const legacyBody = await legacyResponse.json();
  const data = scenarioMapFromLegacyBody(legacyBody);
  return {
    ok: Boolean(data),
    status: legacyResponse.status,
    json: async () => (data ? { success: true, data } : legacyBody)
  };
}

const MIN_LOADING_MS = 1500;

/**
 * Loads the career shift scenario map.
 *
 * Saved results come from GET /api/v2/onboarding-ai/scenario-map and skip the chat
 * transcript. The first ready update waits until the loading screen has been up for
 * 1.5 seconds. When no result is saved, an AI refinement is saved in the background.
 *
 * @param {{ navigate: Function, signal?: AbortSignal, onUpdate: Function }} params
 * `onUpdate` receives `{ onboardingDone, financialData }`.
 */
export async function loadCareerShiftScenarioMap({ navigate, signal, onUpdate }) {
  const openedAt = Date.now();
  let pendingReady = null;
  let readyTimer = null;

  const publish = (update) => {
    if (signal?.aborted) return;
    if (!update.onboardingDone) {
      onUpdate(update);
      return;
    }

    const remaining = MIN_LOADING_MS - (Date.now() - openedAt);
    if (remaining <= 0) {
      pendingReady = null;
      onUpdate(update);
      return;
    }

    pendingReady = update;
    if (readyTimer != null) return;
    readyTimer = setTimeout(() => {
      readyTimer = null;
      if (signal?.aborted || !pendingReady) return;
      const latest = pendingReady;
      pendingReady = null;
      onUpdate(latest);
    }, remaining);
  };

  signal?.addEventListener('abort', () => {
    if (readyTimer != null) clearTimeout(readyTimer);
  }, { once: true });

  const token = getToken();
  if (!token) {
    navigate('/login');
    return;
  }

  try {
    const response = await fetchScenarioMap(token, signal);
    if (signal?.aborted) return;
    if (handleApiResponseError(response, navigate)) return;

    const body = await response.json();
    if (signal?.aborted) return;
    if (!body?.success || !body.data) {
      publish({ onboardingDone: false, financialData: null });
      return;
    }

    const onboardingDone = Boolean(body.data.onboarding_ai_done);
    const cached = toFinancialData(body.data.v2_onboarding_ai_result);
    if (!onboardingDone || cached) {
      publish({ onboardingDone, financialData: cached });
      return;
    }

    const chat = Array.isArray(body.data.chat) ? body.data.chat : [];
    const preview = chat.length > 0 ? toFinancialData(financialDataFromOnboardingChat(chat)) : null;
    publish({ onboardingDone: true, financialData: preview });
    if (chat.length === 0 || signal?.aborted) return;

    try {
      const calculated = await calculateAllFinancialDataAI(chat);
      if (signal?.aborted) return;
      publish({ onboardingDone: true, financialData: toFinancialData(calculated) });
      persistCalculatedResult(token, calculated, navigate);
    } catch (err) {
      if (signal?.aborted || err?.name === 'AbortError') return;
      logger.error('Error calculating financial data:', err);
    }
  } catch (err) {
    if (signal?.aborted || err?.name === 'AbortError') return;
    logger.error('Error loading career shift scenario map:', err);
    handleApiError(err, navigate);
  }
}
